// ИИН Республики Казахстан — 12 цифр (ТЗ §7.1).
// 1–6: дата рождения ГГММДД; 7: век и пол; 12: контрольный разряд.

export type Sex = "male" | "female";

export type IinResult =
  | { valid: true; birthDate: string; sex: Sex }
  | { valid: false; error: string };

const WEIGHTS_1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const WEIGHTS_2 = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2];

export function iinControlDigit(first11: number[]): number | null {
  const sum = (w: number[]) => first11.reduce((acc, d, i) => acc + d * w[i], 0);
  let rest = sum(WEIGHTS_1) % 11;
  if (rest === 10) rest = sum(WEIGHTS_2) % 11;
  return rest === 10 ? null : rest;
}

export function parseIin(raw: string): IinResult {
  const iin = raw.replace(/\s/g, "");
  if (!/^\d{12}$/.test(iin)) return { valid: false, error: "ИИН — 12 цифр" };
  const d = iin.split("").map(Number);

  const centuryDigit = d[6];
  if (centuryDigit < 1 || centuryDigit > 6) return { valid: false, error: "Проверьте ИИН" };
  const century = centuryDigit <= 2 ? 1800 : centuryDigit <= 4 ? 1900 : 2000;
  const year = century + Number(iin.slice(0, 2));
  const month = Number(iin.slice(2, 4));
  const day = Number(iin.slice(4, 6));
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return { valid: false, error: "Проверьте ИИН" };
  }

  const control = iinControlDigit(d.slice(0, 11));
  if (control === null || control !== d[11]) return { valid: false, error: "Проверьте ИИН" };

  return {
    valid: true,
    birthDate: `${year}-${iin.slice(2, 4)}-${iin.slice(4, 6)}`,
    sex: centuryDigit % 2 === 1 ? "male" : "female",
  };
}

/** Builds a valid IIN for tests and seed data. */
export function makeIin(birthDate: string, sex: Sex, serial = 1234): string {
  const [y, m, dd] = birthDate.split("-");
  const yearNum = Number(y);
  const base = yearNum >= 2000 ? 5 : yearNum >= 1900 ? 3 : 1;
  const centuryDigit = sex === "male" ? base : base + 1;
  for (let s = serial; s < serial + 10000; s++) {
    const body = `${y.slice(2)}${m}${dd}${centuryDigit}${String(s % 10000).padStart(4, "0")}`;
    const control = iinControlDigit(body.split("").map(Number));
    if (control !== null) return body + control;
  }
  throw new Error("cannot build IIN");
}

export function ageAt(birthDate: string, at: Date = new Date()): { years: number; days: number } {
  const b = new Date(`${birthDate}T00:00:00Z`);
  let years = at.getUTCFullYear() - b.getUTCFullYear();
  const beforeBirthday =
    at.getUTCMonth() < b.getUTCMonth() || (at.getUTCMonth() === b.getUTCMonth() && at.getUTCDate() < b.getUTCDate());
  if (beforeBirthday) years -= 1;
  const days = Math.floor((at.getTime() - b.getTime()) / 86_400_000);
  return { years, days };
}
